import React, { Component } from "react"
import { connect } from "react-redux"
import { Link } from "react-router-dom"
import { Arwes, Button, Frame as FrameC, Loading, Words } from "arwes"
import styled from "styled-components"
import Icon from "@mdi/react"
import { mdiChemicalWeapon, mdiRobot } from "@mdi/js"
import get from "lodash.get"

import { MarginTop } from "../../common/styled/MarginTop"
import {
  ContentColumn,
  PageBlock as SharedPageBlock,
  PageOffset
} from "../../common/styled/Page"
import { isAdminUser } from "../../common/util/isAdmin"
import NewPost from "../post/NewPost"
import Post from "../post/Post"
import { deleteCell, fetchCell, fetchCellPosts, joinCell } from "./redux/reducers"

const PageBlock = styled(SharedPageBlock)`
  margin: 30px;

  @media (max-width: 900px) {
    margin: 16px 12px;
  }

  @media (max-width: 360px) {
    margin-left: 8px;
    margin-right: 8px;
  }
`

const CellHeading = styled.h3`
  display: flex;
  justify-content: center;
  align-items: baseline;
  gap: 18px;
  flex-wrap: wrap;
  margin: 10px 0;

  @media (max-width: 600px) {
    gap: 8px;
    padding: 0 8px;
  }
`

const CellHeadingName = styled.span`
  min-width: 0;
  color: #ff001c;
  overflow-wrap: anywhere;
  word-break: break-word;
`

const InfoStrip = styled.div`
  padding: 16px 22px;
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    gap: 12px;
    padding: 14px;
  }
`

const InfoText = styled.div`
  flex: 1;
  min-width: 260px;
  color: #d6e1ff;
  line-height: 1.5;
  overflow-wrap: anywhere;
  word-break: break-word;

  @media (max-width: 600px) {
    min-width: 0;
    flex-basis: 100%;
  }
`

const MetaLine = styled.div`
  margin-top: 6px;
  font-family: "VT323", monospace;
  font-size: 0.85rem;
  color: #6fbfff;
`

const Actions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    gap: 8px;
    width: 100%;
  }
`

class CellDetail extends Component {
  state = { show: false }

  componentDidMount() {
    const cellId = this.props.match.params.cellId
    this.props.fetchCell(cellId)
    this.props.fetchCellPosts(cellId)
    setTimeout(() => this.setState({ show: true }), 600)
  }

  componentDidUpdate(prevProps) {
    const prevId = prevProps.match.params.cellId
    const newId = this.props.match.params.cellId
    if (prevId !== newId) {
      this.props.fetchCell(newId)
      this.props.fetchCellPosts(newId)
    }
  }

  handleJoin = () => {
    const { cell } = this.props
    if (cell && !cell.joined) {
      this.props.joinCell(cell._id)
    }
  }

  handleDelete = () => {
    const { cell } = this.props
    if (!cell) return
    const ok = window.confirm(
      `Delete cell #${cell.name}? Posts inside it will be kept but unscoped.`
    )
    if (ok) {
      this.props.deleteCell(cell._id)
    }
  }

  render() {
    const { cell, posts, isLoading, isExpanded, user, isAdmin } = this.props
    const isLoggedIn = !!get(user, "loaded.token")

    if (isLoading && !cell) {
      return (
        <Arwes>
          <PageOffset isExpanded={isExpanded}>
            <div style={{ position: "relative", height: 300 }}>
              <Loading animate full />
            </div>
          </PageOffset>
        </Arwes>
      )
    }

    if (!cell) {
      return (
        <Arwes>
          <PageOffset isExpanded={isExpanded}>
            <PageBlock>
              <FrameC animate level={3} corners={3} layer="alert">
                <div style={{ padding: 24, textAlign: "center" }}>
                  <Words animate layer="alert">Cell not found</Words>
                </div>
              </FrameC>
            </PageBlock>
          </PageOffset>
        </Arwes>
      )
    }

    const createdByName = get(cell, "createdBy.name", "unknown")
    return (
      <Arwes>
        <PageOffset isExpanded={isExpanded}>
          <MarginTop />
          <ContentColumn maxWidth={820}>
            <PageBlock>
              <FrameC
                show={this.state.show}
                animate
                level={2}
                corners={4}
                layer="alert"
              >
                <CellHeading>
                  <CellHeadingName>#{cell.name}</CellHeadingName>
                  <span
                    style={{
                      fontSize: "0.7em",
                      color: "#ffb800",
                      fontFamily: "VT323, monospace",
                      letterSpacing: "0.1em"
                    }}
                  >
                    {(cell.memberCount || 0).toLocaleString()} MEMBERS
                  </span>
                </CellHeading>
              </FrameC>
            </PageBlock>

            <PageBlock>
              <FrameC
                show={this.state.show}
                animate
                level={3}
                corners={3}
                layer="primary"
              >
                <InfoStrip>
                  <InfoText>
                    <div>{cell.description}</div>
                    <MetaLine>
                      created by{" "}
                      <span style={{ color: "#00e7ff" }}>{createdByName}</span>{" "}
                      · {cell.visibility}
                    </MetaLine>
                  </InfoText>
                  <Actions>
                    {isLoggedIn && cell.joined && (
                      <Button animate layer="success" disabled>
                        <Icon
                          path={mdiChemicalWeapon}
                          size={0.5}
                          color="#17ff0e"
                          spin
                        />{" "}
                        Joined
                      </Button>
                    )}
                    {isLoggedIn && !cell.joined && (
                      <Button animate layer="primary" onClick={this.handleJoin}>
                        <Icon
                          path={mdiChemicalWeapon}
                          size={0.5}
                          color="#be26fc"
                          spin
                        />{" "}
                        Join Cell
                      </Button>
                    )}
                    {isAdmin && (
                      <Button animate layer="alert" onClick={this.handleDelete}>
                        <Icon path={mdiRobot} size={0.5} color="#ff001c" /> Delete Cell
                      </Button>
                    )}
                    <Link
                      to="/cells"
                      style={{ textDecoration: "none" }}
                    >
                      <Button animate layer="control">Back</Button>
                    </Link>
                  </Actions>
                </InfoStrip>
              </FrameC>
            </PageBlock>

            {isLoggedIn && cell.joined && (
              <PageBlock>
                <NewPost cellId={cell._id} />
              </PageBlock>
            )}

            <PageBlock>
              {(!posts || posts.length === 0) && (
                <FrameC animate level={3} corners={3} layer="disabled">
                  <div
                    style={{
                      padding: 24,
                      textAlign: "center",
                      color: "#6fbfff",
                      fontFamily: "VT323, monospace"
                    }}
                  >
                    No transmissions in this cell yet.
                  </div>
                </FrameC>
              )}
              {posts &&
                posts.length > 0 &&
                posts.map(p => (
                  <Link
                    key={p._id}
                    to={`/post/${p._id}`}
                    style={{ textDecoration: "none" }}
                  >
                    <Post post={p} />
                  </Link>
                ))}
            </PageBlock>
          </ContentColumn>
        </PageOffset>
      </Arwes>
    )
  }
}

const mapState = state => ({
  cell: state.cells.single,
  posts: state.cells.posts,
  isLoading: state.cells.isLoading,
  user: state.user,
  isAdmin: isAdminUser(state)
})

export default connect(
  mapState,
  { fetchCell, fetchCellPosts, joinCell, deleteCell }
)(CellDetail)
